// src/components/admin/GhlSection.tsx
// Admin section for managing GHL pipeline connections and triggering CRM syncs.
import { GhlAddConnectionForm } from "./ghl/GhlAddConnectionForm";
import { GhlConnectionCard } from "./ghl/GhlConnectionCard";
import { GhlHelpModal } from "./ghl/GhlHelpModal";
import { blankConn, blankPipe } from "./ghl/ghlSectionShared";
import { useGhlSection } from "@/components/admin/useGhlSection";

export function GhlSection({
  organizationId,
  orgName,
  plain,
}: {
  organizationId: string;
  orgName?: string;
  plain?: boolean;
}) {
  const vm = useGhlSection(organizationId, orgName);

  return (
    <section className={plain ? "space-y-4" : "p-4 rounded-xl bg-white border border-gray-200 space-y-4"}>
      <div className="flex items-center justify-between">
        {!plain && <h2 className="text-sm font-semibold text-gray-700">GHL CRM Connections</h2>}
        <button
          type="button"
          onClick={() => vm.setShowHelp(true)}
          className="h-5 w-5 rounded-full bg-gray-200 text-gray-600 text-xs font-bold hover:bg-gray-300 flex items-center justify-center"
          title="How to use this section"
        >
          ?
        </button>
      </div>

      <GhlHelpModal open={vm.showHelp} onClose={() => vm.setShowHelp(false)} />

      <div className="flex justify-center">
        <button
          type="button"
          onClick={() => void vm.triggerSync()}
          disabled={vm.syncing || vm.loading || vm.connections.filter((c) => c.is_active).length === 0}
          className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700 disabled:opacity-50"
        >
          {vm.syncing ? "Syncing…" : "Sync all"}
        </button>
      </div>

      <div className="text-xs bg-blue-50 text-blue-800 border border-blue-200 rounded-lg px-3 py-2">
        <span className="font-semibold">GHL Workflow Routing</span>
        {" — "}
        Each lead is tagged with a location slug (e.g.{" "}
        <code className="font-mono bg-blue-100 px-1 rounded">ravenel</code>) and product type (e.g.{" "}
        <code className="font-mono bg-blue-100 px-1 rounded">sheds</code>). Route leads in GHL automations using these tags.{" "}
        <span className="text-blue-600">
          See <em>GHL Workflow Routing Guide</em> in the KB.
        </span>
      </div>

      {vm.msg && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{vm.msg}</div>
      )}

      {vm.syncResult && (
        <div className="text-sm bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 space-y-1">
          <div className="font-medium text-emerald-800">
            Sync complete — {vm.syncResult.synced} opportunities across {vm.syncResult.connections} connection
            {vm.syncResult.connections !== 1 ? "s" : ""}
          </div>
          {vm.syncResult.results.map((r, i) => (
            <div key={i} className="text-emerald-700 text-xs">
              {r.connection} › {r.pipeline}:{" "}
              {r.error ? (
                <span className="text-red-600">{r.error}</span>
              ) : (
                `${r.synced} upserted${r.stagesFound !== undefined ? ` · ${r.stagesFound} stages resolved` : ""}`
              )}
            </div>
          ))}
        </div>
      )}

      {!vm.loading && vm.connections.length === 0 && (
        <div className="text-sm text-gray-500">No GHL connections yet. Add one below.</div>
      )}

      {vm.connections.map((conn) => (
        <GhlConnectionCard
          key={conn.id}
          conn={conn}
          pipes={vm.pipelinesByConn[conn.id] ?? []}
          collapsed={vm.collapsedConns.has(conn.id)}
          onToggleCollapse={() => vm.toggleCollapse(conn.id)}
          locationLabel={vm.locationName}
          loading={vm.loading}
          editWebhookOpen={vm.editWebhookFor === conn.id}
          editWebhookValue={vm.editWebhookValue}
          onEditWebhookToggle={() => {
            vm.setEditWebhookFor(vm.editWebhookFor === conn.id ? null : conn.id);
            vm.setEditWebhookValue(conn.webhook_url ?? "");
            vm.setEditKeyFor(null);
            vm.setEditKeyValue("");
          }}
          onEditWebhookValueChange={vm.setEditWebhookValue}
          onSaveWebhook={() => void vm.updateWebhookUrl(conn.id)}
          onCancelWebhook={() => {
            vm.setEditWebhookFor(null);
            vm.setEditWebhookValue("");
          }}
          editKeyOpen={vm.editKeyFor === conn.id}
          editKeyValue={vm.editKeyValue}
          onEditKeyToggle={() => {
            vm.setEditKeyFor(vm.editKeyFor === conn.id ? null : conn.id);
            vm.setEditKeyValue("");
            vm.setEditWebhookFor(null);
            vm.setEditWebhookValue("");
          }}
          onEditKeyValueChange={vm.setEditKeyValue}
          onSaveKey={() => void vm.updateApiKey(conn.id)}
          onCancelKey={() => {
            vm.setEditKeyFor(null);
            vm.setEditKeyValue("");
          }}
          onToggleConnection={() => void vm.toggleConnection(conn.id, conn.is_active, conn.company_name)}
          onTogglePipeline={(pipelineId, isActive, name) => void vm.togglePipeline(pipelineId, isActive, name)}
          showAddPipe={vm.showAddPipeFor === conn.id}
          newPipe={vm.newPipe}
          setNewPipe={vm.setNewPipe}
          loadingPipes={!!vm.loadingPipes[conn.id]}
          availPipelines={vm.availPipelines[conn.id] ?? []}
          knownProductTypes={vm.knownProductTypes}
          locations={vm.locations}
          autofillMsg={vm.autofillMsg}
          onOpenAddPipeline={() => void vm.openAddPipeline(conn.id)}
          onAddPipeline={() => void vm.addPipeline(conn.id)}
          onCancelAddPipeline={() => {
            vm.setShowAddPipeFor(null);
            vm.setNewPipe(blankPipe);
            vm.setAutofillMsg(null);
          }}
          tryAutofillFromName={vm.tryAutofillFromName}
          canTogglePipelineActive={vm.canTogglePipelineActive}
        />
      ))}

      {vm.showAddConn ? (
        <GhlAddConnectionForm
          newConn={vm.newConn}
          setNewConn={vm.setNewConn}
          loading={vm.loading}
          onSave={() => void vm.addConnection()}
          onCancel={() => {
            vm.setShowAddConn(false);
            vm.setNewConn(blankConn);
          }}
        />
      ) : (
        <button
          type="button"
          onClick={() => vm.setShowAddConn(true)}
          className="w-full text-left px-4 py-3 rounded-lg border border-dashed border-gray-300 text-sm text-indigo-600 hover:bg-indigo-50 font-medium"
        >
          + Add GHL connection
        </button>
      )}
    </section>
  );
}
